import Head from "next/head";
import { connect } from "react-redux";
import ErrorPage from "@containers/ErrorPage";
import getConfig from "next/config";
import withRouterPageLayout from "@components/withRouterPageLayout.js";
import Button from "@components/Button";
import SubscribeForm from "@components/Marketplace/SubscribeForm";
import ContactForm from "@components/Marketplace/ContactForm";

const {
  publicRuntimeConfig: {
    content: { websiteTitle },
    settings: {
      websiteStatics,
      baseUrl,
      marketplace: { mktpSubscribeId, mktpContactId },
    },
  },
} = getConfig();

const cards = [
  {
    id: 0,
    title: "produse vandute anual",
    icon: `${websiteStatics}/images/marketplace/sold-products.svg`,
    count: 22,
    unit: "milioane",
  },
  {
    id: 1,
    title: "vizite unice",
    icon: `${websiteStatics}/images/marketplace/unique-visits.svg`,
    count: 45,
    unit: "milioane",
  },
  {
    id: 2,
    title: "afisari",
    icon: `${websiteStatics}/images/marketplace/views.svg`,
    count: 180,
    unit: "milioane",
  },
];

const Marketplace = ({ user }) => {
  return websiteTitle === "Altex" ? (
    <>
      <Head>
        <title>
          Altex Marketplace - Scaleaza-ti afacerea la nivelul urmator!
        </title>
        <meta
          name="description"
          content="Marketplace-ul Altex iti ofera o alternativa optima si eficienta de a comercializa produsele afacerii tale si de a te adresa unei noi baze de clienti din toata Romania!"
        />
        <meta name="keywords" content="marketplace, vendor, seller, mktp" />
      </Head>

      <div className="flex flex-col items-center justify-center w-full">
        <div className="relative w-full">
          <img
            className="block h-[500px] md:h-[354px] w-full object-cover object-right"
            src={`${websiteStatics}/images/marketplace/headline-background-mobile.png`}
            srcSet={`${websiteStatics}/images/marketplace/headline-background-mobile.png 768w, ${websiteStatics}/images/marketplace/headline-background.png 1270w`}
            alt="Dezvolta-ti propria afacere pe Altex Marketplace"
            draggable={false}
          />

          <div className="absolute bottom-0 left-0 md:top-0 p-4 md:p-20 flex flex-col items-start w-full md:w-2/3">
            <h1 className="text-3xl md:text-4xl xl:text-5xl text-white font-medium w-full">
              Altex Marketplace
            </h1>

            <h2 className="w-full max-w-sm md:max-w-full md:w-2/3 pr-10 lg:pr-14 leading-5 md:leading-7 md:text-xl xl:text-2xl text-black font-semibold md:font-medium">
              Dezvolta-ti propria afacere pe Altex Marketplace
            </h2>

            <div className="mt-2 md:mt-4 w-full md:w-44">
              <Button
                title="Inscrie-te acum"
                display="block"
                font="medium"
                size="small"
                isOrderAgain={true}
                variant="secondary"
                hasMinWidth={false}
                href="#formular-de-inscriere"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center w-full bg-[#F1F1F1]">
          <div className="flex flex-col md:flex-row items-center justify-center w-full md:space-x-4 space-y-3 md:space-y-0 px-3 md:px-0 py-4 md:py-14 md:max-w-2xl xl:max-w-4xl">
            {cards.map((icon) => (
              <Card key={icon.id} {...icon} />
            ))}
          </div>
        </div>

        <div className="flex flex-col-reverse md:flex-row items-center justify-center w-full pt-12 lg:pt-16 md:max-w-2xl xl:max-w-4xl px-2 md:px-0">
          <div className="flex flex-col items-start justify-start w-full md:w-2/3 space-y-5">
            <h3
              id="de-ce-sa-vinzi-pe-altex-marketplace"
              className="w-full leading-7 text-xl xl:text-[28px] text-black font-semibold md:font-medium text-left"
            >
              De ce sa iei in calcul marketplace-ul Altex ca o noua platforma de
              vanzari?
            </h3>

            <ul className="list-[disc] marker:text-yellow-text marker:text-2xl pl-5 md:pl-6 md:pr-24">
              <li className="text-sm xl:text-base leading-6 xl:leading-7">
                Ai oportunitatea sa iti vinzi produsele pe unul dintre cele mai
                cunoscute canale de vanzare din Romania
              </li>
              <li className="text-sm xl:text-base leading-6 xl:leading-7">
                Ai acces la o baza de clienti stabila, in continua crestere
              </li>
              <li className="text-sm xl:text-base leading-6 xl:leading-7">
                Procesul de integrare este rapid si simplu
              </li>
              <li className="text-sm xl:text-base leading-6 xl:leading-7">
                Suport personalizat pe toata durata parteneriatului, prin echipa
                de specialisti in ecommerce
              </li>
              <li className="text-sm xl:text-base leading-6 xl:leading-7">
                Comision unic 8% pentru comenzile a caror valoare depaseste 100
                lei
              </li>
              <li className="text-sm xl:text-base leading-6 xl:leading-7">
                Optiunea de livrare prin curierul Altex, fara costuri pentru
                tine
              </li>
            </ul>

            <div className="w-full md:w-44">
              <Button
                title="Inscrie-te acum"
                display="block"
                isOrderAgain={true}
                variant="secondary"
                hasMinWidth={false}
                href="#formular-de-inscriere"
              />
            </div>
          </div>

          <div className="flex items-center justify-center mb-10 md:mb-0 w-full md:w-1/3">
            <img
              className="block w-48 xl:w-60 select-none"
              src={`${websiteStatics}/images/marketplace/why-marketplace.svg`}
              alt="De ce sa vinzi pe Altex Marketplace?"
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center w-full pt-24 md:max-w-2xl xl:max-w-4xl px-2 md:px-0">
          <div className="flex items-center justify-center mb-10 md:mb-0 w-full md:w-1/3">
            <img
              className="block w-48 xl:w-60 select-none"
              src={`${websiteStatics}/images/marketplace/steps-marketplace.svg`}
              alt="De ce sa vinzi pe Altex Marketplace?"
            />
          </div>

          <div className="flex flex-col items-start justify-start w-full md:w-2/3 space-y-5 md:pl-28">
            <h3
              id="care-sunt-pasii-pentru-a-vinde-pe-altex-marketplace?"
              className="w-full leading-7 text-xl xl:text-[28px] text-black font-semibold md:font-medium"
            >
              Altex, marketplace-ul din Romania care te ajuta sa-ti scalezi
              vanzarile - Care sunt pasii?
            </h3>

            <ul className="list-[disc] marker:text-yellow-text marker:text-2xl pl-5 md:pl-6">
              <li className="text-sm xl:text-base leading-6 xl:leading-7">
                Completeaza formularul “Inscrie-te acum”
              </li>
              <li className="text-sm xl:text-base leading-6 xl:leading-7">
                Asteapta evaluarea specialistilor nostri si instructiuni pentru
                pasii urmatori
              </li>
              <li className="text-sm xl:text-base leading-6 xl:leading-7">
                Incepe sa vinzi produsele tale pe Altex Marketplace
              </li>
            </ul>

            <small>
              *Prioritizam companiile cu minim doi ani vechime, cu produse
              relevante pentru categoriile Jucarii & Bebe, Sport, Mobila,
              Decoratiuni, Ingrijirea Casei , Bricolaj, Auto, Produse pentru
              animale si Bauturi si o cifra de afaceri echivalenta a minim
              300,000 EUR in ultimul an.
            </small>

            <div className="flex flex-col md:flex-row items-center justify-center md:space-x-4 space-y-2 md:space-y-0 w-full">
              <Button
                title="Inscrie-te acum"
                display="block"
                isOrderAgain={true}
                variant="secondary"
                hasMinWidth={false}
                href="#formular-de-inscriere"
              />
              <p className="text-xs font-medium">sau</p>
              <Button
                title="Contacteaza-ne"
                display="block"
                isOrderAgain={true}
                variant="primary"
                hasMinWidth={false}
                href="#formular-de-contact"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse md:flex-row items-center justify-center w-full pt-24 md:max-w-2xl xl:max-w-4xl px-2 md:px-0">
          <div className="flex flex-col items-start justify-start w-full md:w-2/3 space-y-5">
            <div
              id="formular-de-inscriere"
              className="w-full leading-7 text-xl xl:text-[28px] text-black font-semibold md:font-medium text-left"
            >
              Formular de inscriere
            </div>

            <SubscribeForm
              user={user}
              baseUrl={baseUrl}
              formId={mktpSubscribeId}
            />
          </div>

          <div className="flex items-center justify-center mb-10 md:mb-0 w-full md:w-1/3">
            <img
              className="block w-48 xl:w-60 select-none"
              src={`${websiteStatics}/images/marketplace/subscribe-form.svg`}
              alt="Formular de inscriere"
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center w-full pt-24 md:max-w-2xl xl:max-w-4xl px-2 md:px-0">
          <div className="flex items-center justify-center mb-10 md:mb-0 w-full md:w-1/3">
            <img
              className="block w-48 xl:w-60 select-none"
              src={`${websiteStatics}/images/marketplace/contact-form.svg`}
              alt="Formular de contact"
            />
          </div>

          <div className="flex flex-col items-start justify-start w-full space-y-5">
            <div
              id="formular-de-contact"
              className="w-full leading-7 text-xl xl:text-[28px] text-black font-semibold md:font-medium text-left md:pl-32"
            >
              Formular de contact
            </div>

            <ContactForm user={user} formId={mktpContactId} />
          </div>
        </div>
      </div>
    </>
  ) : (
    <ErrorPage layout="404">
      Ne pare rau, dar pagina cautata nu a fost gasita
    </ErrorPage>
  );
};

const Card = ({ title, icon, count, unit }) => (
  <div className="flex flex-col self-stretch items-center justify-center w-full md:max-w-[230px] py-6 xl:py-10 bg-white rounded-lg ">
    <img
      className="w-14 xl:w-20 !h-14 xl:!h-20 p-1 select-none"
      src={icon}
      alt={title}
    />

    <p className="text-5xl leading-10 pt-2 md:leading-normal md:pt-0 font-medium h-1/4 mb-0 md:mb-2 xl:mb-0">
      {count}
    </p>

    <p className="h-1/5 leading-10 md:leading-normal pb-2 md:pb-0 text-xl xl:text-2xl font-medium">
      {unit}
    </p>

    <div className="text-sm xl:text-base font-semibold h-1/5 text-[#ADADAD] text-center px-2.5 xl:px-6">
      {title}
    </div>
  </div>
);

Marketplace.pageLayout = "default";

export default withRouterPageLayout(
  connect((state) => ({
    user: state.user,
  }))(Marketplace)
);
